import { MagicWand } from '@carbon/icons-react';
import {
  Button,
  DataTable,
  DataTableSkeleton,
  InlineNotification,
  Modal,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
} from '@carbon/react';
import { asProblem, problemMessage, useReferenceOptions } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { hoApi } from '../../api';
import { fromSuggestion, toRequest } from './variantDraft';

interface Suggestion {
  name: string;
  code: string;
  slug: string;
  members: { itemId: string; itemCode: string; itemName: string; value: string; isDefault: boolean }[];
}

/**
 * "Suggest groups" (ADR 0052): items of a brand whose names differ only by a size tag, e.g. "Margherita (R)" and
 * "Margherita (L)", proposed as groups with one size dimension. The user ticks the ones to create; nothing is grouped
 * on its own.
 */
export function SuggestGroupsButton({ refresh }: { refresh(): void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const brands = useReferenceOptions('org.brand');
  const [brandId, setBrandId] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const brand = brandId || brands.data?.[0]?.value || '';
  const suggestions = useQuery({
    queryKey: ['ho', 'item-group-suggestions', brand],
    enabled: open && Boolean(brand),
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/catalog/item-groups/suggestions', { params: { query: { brandId: brand } } });
      if (!data) throw error;
      return data satisfies Suggestion[];
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      for (const s of suggestions.data?.filter((x) => picked.has(x.code)) ?? []) {
        const made = await hoApi.POST('/api/v1/catalog/item-groups', {
          body: { brandId: brand, code: s.code, name: s.name, slug: s.slug } as never,
        });
        const group = made.data as { id: string; version: number } | undefined;
        if (!group) throw made.error;
        const saved = await hoApi.PUT('/api/v1/catalog/item-groups/{id}/variants', {
          params: { path: { id: group.id } },
          headers: { 'If-Match': `"${group.version}"` },
          body: toRequest(fromSuggestion(s.members)),
        });
        if (!saved.data) throw saved.error;
      }
    },
    onSuccess: () => {
      setOpen(false);
      setPicked(new Set());
      refresh();
    },
  });

  const headers = [
    { key: 'name', header: t('md.fields.name') },
    { key: 'variants', header: t('itemGroups.members') },
  ];
  const rows = (suggestions.data ?? []).map((s) => ({
    id: s.code,
    isSelected: picked.has(s.code),
    name: s.name,
    variants: s.members.map((m) => `${m.value}${m.isDefault ? ` (${t('md.fields.is_default')})` : ''}`).join(' · '),
  }));
  const toggle = (code: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <>
      <Button kind="tertiary" renderIcon={MagicWand} onClick={() => setOpen(true)}>
        {t('itemGroups.suggest.button')}
      </Button>
      {/* In a portal: the list toolbar is a containing block for fixed elements, which would trap the modal inside the table. */}
      {open &&
        createPortal(
          <Modal
            open
            modalHeading={t('itemGroups.suggest.title')}
            primaryButtonText={t('itemGroups.suggest.create', { count: picked.size })}
            secondaryButtonText={t('common.cancel')}
            primaryButtonDisabled={picked.size === 0 || create.isPending}
            onRequestClose={() => setOpen(false)}
            onRequestSubmit={() => create.mutate()}
            size="md"
          >
            <div className="pos-item-group__suggest">
              <p className="pos-item-group__hint">{t('itemGroups.suggest.help')}</p>
              {(brands.data?.length ?? 0) > 1 && (
                <Select id="ig-suggest-brand" labelText={t('md.fields.brand_id')} value={brand} onChange={(e) => setBrandId(e.target.value)}>
                  {brands.data?.map((b) => (
                    <SelectItem key={b.value} value={b.value} text={b.label} />
                  ))}
                </Select>
              )}
              {create.error && <InlineNotification kind="error" lowContrast hideCloseButton title={problemMessage(t, asProblem(create.error))} />}
              {suggestions.isLoading ? (
                <DataTableSkeleton columnCount={2} rowCount={4} showHeader={false} showToolbar={false} />
              ) : rows.length === 0 ? (
                <p>{t('itemGroups.suggest.none')}</p>
              ) : (
                <DataTable rows={rows} headers={headers} size="md">
                  {({ rows: tableRows, headers: tableHeaders, getHeaderProps, getRowProps, getSelectionProps, getTableProps, getTableContainerProps }) => (
                    <TableContainer {...getTableContainerProps()}>
                      <Table {...getTableProps()} aria-label={t('itemGroups.suggest.title')}>
                        <TableHead>
                          <TableRow>
                            <TableSelectAll
                              {...getSelectionProps()}
                              checked={picked.size === rows.length}
                              indeterminate={picked.size > 0 && picked.size < rows.length}
                              onSelect={() => setPicked(picked.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)))}
                            />
                            {tableHeaders.map((header) => {
                              const { key, ...props } = getHeaderProps({ header });
                              return (
                                <TableHeader key={key} {...props}>
                                  {header.header}
                                </TableHeader>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tableRows.map((row) => {
                            const { key, ...props } = getRowProps({ row });
                            return (
                              <TableRow key={key} {...props}>
                                <TableSelectRow {...getSelectionProps({ row })} checked={picked.has(row.id)} onSelect={() => toggle(row.id)} />
                                {row.cells.map((cell) => (
                                  <TableCell key={cell.id}>{cell.value}</TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              )}
            </div>
          </Modal>,
          document.body,
        )}
    </>
  );
}
